import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Users, Briefcase, TrendingUp, Building2, CreditCard, Clock, ArrowUpRight, ArrowDownRight, Wallet, Percent } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatClientCode } from '@/lib/clientCode';

const COLORS = ['#2563EB', '#3B82F6', '#6366F1', '#8B5CF6', '#06B6D4'];
const CHART_AXIS_TICK = { fontSize: 12, fill: '#51635C' };

const roleLabels = {
  super_admin: 'Super Admin', admin: 'Admin', manager: 'Manager',
  trader: 'Trader', office_staff: 'Office Staff', receptionist: 'Receptionist'
};

const MetricCard = ({ title, value, icon: Icon, prefix = '$' }) => (
  <Card className="metric-card border border-brand-border bg-white opacity-0 animate-fade-in" data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary mb-2">{title}</p>
          <p className="font-heading text-3xl tracking-tight text-brand-text-primary">
            {prefix}{typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : value}
          </p>
        </div>
        <div className="w-10 h-10 rounded-sm bg-brand-primary/10 flex items-center justify-center">
          <Icon size={20} strokeWidth={1.5} className="text-brand-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-brand-border rounded-sm p-3 shadow-sm text-sm">
      <p className="font-medium text-brand-text-primary mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name || p.dataKey} style={{ color: p.color }} className="text-xs">
          {p.name}: ${Number(p.value).toLocaleString()}
        </p>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { user, permissions } = useAuth();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [branchData, setBranchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const role = user?.role;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [api.get('/dashboard'), api.get('/dashboard/charts')];
        if (role === 'trader') promises.push(api.get('/revenue/trader-income/summary'));
        if (role === 'manager') promises.push(api.get('/revenue/manager-incentive/summary'));
        if (['super_admin', 'admin'].includes(role)) promises.push(api.get('/branches').catch(() => ({ data: [] })));
        const results = await Promise.all(promises);
        setStats(results[0].data);
        setCharts(results[1].data);
        if (role === 'trader' && results[2]) setIncomeSummary(results[2].data);
        if (role === 'manager' && results[2]) setIncomeSummary(results[2].data);
        if (['super_admin', 'admin'].includes(role) && results[2]) setBranches(results[2].data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [role]);

  useEffect(() => {
    if (selectedBranch && selectedBranch !== 'all') {
      api.get(`/dashboard/branch/${selectedBranch}`).then(res => setBranchData(res.data)).catch(() => setBranchData(null));
    } else {
      setBranchData(null);
    }
  }, [selectedBranch]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;

  const isFinanceVisible = permissions?.funds?.view || permissions?.pnl?.view;
  const isFullAdmin = ['super_admin', 'admin'].includes(role);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Dashboard</h1>
          <p className="text-sm text-brand-text-secondary mt-1">
            {isFullAdmin ? 'System overview and analytics' :
             role === 'manager' ? 'Branch performance overview' :
             role === 'trader' ? 'Your daily work panel' :
             role === 'receptionist' ? 'Client onboarding dashboard' :
             'Overview'}
          </p>
        </div>
        <Badge className="bg-brand-primary/10 text-brand-primary border-0 px-3 py-1 text-xs">{roleLabels[role] || role}</Badge>
      </div>

      {/* Branch Filter - Admin Only */}
      {isFullAdmin && branches.length > 0 && (
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">View Branch:</span>
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-48 bg-white border-brand-border rounded-sm text-sm" data-testid="branch-filter-select"><SelectValue placeholder="All Branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Branch-Specific Dashboard */}
      {isFullAdmin && branchData && (
        <Card className="border border-brand-border bg-white" data-testid="branch-dashboard-panel">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium text-brand-text-primary flex items-center gap-2">
              <Building2 size={18} className="text-brand-primary" /> {branchData.branch?.name} — Branch Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              {[
                { l: 'Active Clients', v: branchData.stats?.active_clients },
                { l: 'Active Traders', v: branchData.stats?.active_traders },
                { l: 'Net P&L', v: `$${(branchData.stats?.net_pnl || 0).toLocaleString()}`, green: branchData.stats?.net_pnl >= 0 },
                { l: 'Trader Income', v: `$${(branchData.stats?.total_trader_income || 0).toLocaleString()}` },
                { l: 'Manager Incentive', v: `$${(branchData.stats?.total_manager_incentive || 0).toLocaleString()}` },
              ].map(item => (
                <div key={item.l} className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-0.5">{item.l}</p>
                  <p className={`font-heading text-lg ${item.green === false ? 'text-brand-error' : item.green === true ? 'text-brand-success' : 'text-brand-text-primary'}`}>{item.v}</p>
                </div>
              ))}
            </div>
            {branchData.manager && (
              <p className="text-xs text-brand-text-secondary">Manager: <span className="font-medium text-brand-text-primary">{branchData.manager.name}</span></p>
            )}
            {branchData.clients?.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-brand-border">{['Client ID', 'Name', 'Balance', 'Net P&L', 'Status'].map(h => <th key={h} className="text-left py-2 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}</tr></thead>
                  <tbody>{branchData.clients.map(c => (
                    <tr key={c.id} className="border-b border-brand-border/50">
                      <td className="py-2 px-3 font-mono text-xs text-brand-primary font-semibold">{formatClientCode(c)}</td>
                      <td className="py-2 px-3 text-brand-text-primary">{c.name}</td>
                      <td className="py-2 px-3 font-medium">${c.balance?.toLocaleString()}</td>
                      <td className={`py-2 px-3 font-medium ${c.net_pnl >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${c.net_pnl?.toLocaleString()}</td>
                      <td className="py-2 px-3"><Badge className={`text-xs border-0 ${c.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{c.status}</Badge></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Metric Cards - Role Aware */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isFinanceVisible && <div className="stagger-1"><MetricCard title="Total AUM" value={stats?.total_aum || 0} icon={DollarSign} /></div>}
        {isFinanceVisible && <div className="stagger-2"><MetricCard title="Net P&L" value={stats?.net_pnl || 0} icon={TrendingUp} /></div>}
        <div className="stagger-3"><MetricCard title="Active Clients" value={stats?.active_clients || 0} icon={Users} prefix="" /></div>
        {(isFullAdmin || role === 'manager') && <div className="stagger-4"><MetricCard title="Active Traders" value={stats?.active_traders || 0} icon={Briefcase} prefix="" /></div>}
      </div>

      {/* Secondary Metrics - Admin/Manager only */}
      {(isFullAdmin || role === 'manager') && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard title="Branches" value={stats?.total_branches || 0} icon={Building2} prefix="" />
          <MetricCard title="Trading Accounts" value={stats?.total_accounts || 0} icon={CreditCard} prefix="" />
          {isFinanceVisible && <MetricCard title="Pending Approvals" value={stats?.pending_transactions || 0} icon={Clock} prefix="" />}
        </div>
      )}

      {/* Trader Income Summary */}
      {role === 'trader' && incomeSummary && (
        <Card className="border border-brand-border bg-white" data-testid="trader-income-summary">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium text-brand-text-primary flex items-center gap-2">
              <Wallet size={18} strokeWidth={1.5} className="text-brand-primary" />
              Trader Income Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Total Earnings</p>
                <p className="font-heading text-2xl text-brand-text-primary">${incomeSummary.total_earning?.toLocaleString() || '0'}</p>
              </div>
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Monthly Earnings</p>
                <p className="font-heading text-2xl text-brand-primary">${incomeSummary.monthly_earning?.toLocaleString() || '0'}</p>
              </div>
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Today's Earnings</p>
                <p className="font-heading text-2xl text-brand-accent">${incomeSummary.today_earning?.toLocaleString() || '0'}</p>
              </div>
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Income Entries</p>
                <p className="font-heading text-2xl text-brand-text-primary">{incomeSummary.entries || 0}</p>
              </div>
            </div>
            <p className="text-xs text-brand-text-secondary mt-3">Your income is auto-calculated from the profit-sharing system based on client performance.</p>
          </CardContent>
        </Card>
      )}

      {/* Manager Branch Income Summary */}
      {role === 'manager' && incomeSummary && (
        <Card className="border border-brand-border bg-white" data-testid="manager-income-summary">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium text-brand-text-primary flex items-center gap-2">
              <Percent size={18} strokeWidth={1.5} className="text-brand-primary" />
              Manager Branch Income Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Total Incentive Earned</p>
                <p className="font-heading text-2xl text-brand-text-primary">${incomeSummary.total_incentive?.toLocaleString() || '0'}</p>
              </div>
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">This Month</p>
                <p className="font-heading text-2xl text-brand-primary">${incomeSummary.monthly_incentive?.toLocaleString() || '0'}</p>
              </div>
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Incentive Records</p>
                <p className="font-heading text-2xl text-brand-text-primary">{incomeSummary.records || 0}</p>
              </div>
            </div>
            {incomeSummary.branch_name && (
              <p className="text-xs text-brand-text-secondary mt-3">Branch: <span className="font-medium text-brand-text-primary">{incomeSummary.branch_name}</span> — Your incentive is auto-calculated from branch-level profit performance.</p>
            )}
            {!incomeSummary.branch_name && (
              <p className="text-xs text-brand-text-secondary mt-3">Your incentive is auto-calculated from branch-level profit performance.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* P&L Trend Chart */}
        <Card className="lg:col-span-2 border border-brand-border bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium text-brand-text-primary">Monthly P&L Trend</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {charts?.monthly_pnl?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={charts.monthly_pnl}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
                  <XAxis dataKey="month" tick={CHART_AXIS_TICK} />
                  <YAxis tick={CHART_AXIS_TICK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="profit" name="Profit" stroke="#2563EB" fill="#2563EB" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="loss" name="Loss" stroke="#DC2626" fill="#DC2626" fillOpacity={0.1} strokeWidth={2} />
                  <Area type="monotone" dataKey="net" name="Net P&L" stroke="#059669" fill="#059669" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-brand-text-secondary text-sm">
                No P&L data yet. Start adding entries to see trends.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Branch Distribution */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium text-brand-text-primary">Client Distribution</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {charts?.branch_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={charts.branch_distribution} dataKey="clients" nameKey="branch" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                    {charts.branch_distribution.map((d, i) => (
                      <Cell key={d.branch || `slice-${i}`} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-brand-text-secondary text-sm">
                No branch data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg font-medium text-brand-text-primary">Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {charts?.recent_transactions?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-brand-border">
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">Type</th>
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">Amount</th>
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">Status</th>
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {charts.recent_transactions.map((txn, i) => (
                    <tr key={txn.id || i} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors duration-150">
                      <td className="py-3 px-2 capitalize text-brand-text-primary">{txn.type}</td>
                      <td className="py-3 px-2 font-medium text-brand-text-primary">${Number(txn.amount).toLocaleString()}</td>
                      <td className="py-3 px-2">
                        <Badge variant={txn.status === 'approved' ? 'default' : txn.status === 'pending' ? 'secondary' : 'destructive'}
                          className={`text-xs ${txn.status === 'approved' ? 'bg-brand-success/15 text-brand-success border-0' : txn.status === 'pending' ? 'bg-amber-100 text-amber-700 border-0' : ''}`}>
                          {txn.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-brand-text-secondary">{txn.created_at ? new Date(txn.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-brand-text-secondary text-sm py-8 text-center">No transactions yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
